import React, { createContext, useCallback, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import notificationFactory from '../components/Notification/notificationFactory';
import Notification from '../components/Notification/Notification';
import { isFulfilledAction } from '../actions/apiActionHelper';

const NotificationsContext = createContext(null);

export function NotificationsProvider({ children }) {
  const notificationRef = React.useRef();

  if (!notificationRef.current) {
    notificationRef.current = notificationFactory({
      duration: 5,
    });
  }

  useEffect(() => () => notificationRef.current?.destroy(), []);

  return <NotificationsContext.Provider value={notificationRef.current}>{children}</NotificationsContext.Provider>;
}

export default function useNotifications() {
  const { t } = useTranslation();
  const notification = useContext(NotificationsContext);

  const showNotice = useCallback(
    ({ type, message, localeOptions = {} }) => {
      notification.notice(
        <Notification type={type} localeOptions={localeOptions}>
          {t(message, { ...localeOptions })}
        </Notification>
      );
    },
    [t, notification]
  );

  const showError = useCallback(
    (message = 'Something went wrong') => {
      showNotice({
        type: 'error',
        message: String(message),
      });
    },
    [showNotice]
  );

  const resolveWithNotice = useCallback(
    (promise, message, localeOptions = {}) =>
      promise
        .then(() => {
          showNotice({ message, localeOptions, type: 'success' });
          return { success: true };
        })
        .catch(() => {
          showError();
          return { error: true };
        }),
    [showError, showNotice]
  );

  const resolveWithAction = useCallback(
    (dispatchAction, message, localeOptions) =>
      dispatchAction.then((action) => {
        if (isFulfilledAction(action)) {
          showNotice({ message, localeOptions, type: 'success' });
          return { success: true };
        } else {
          showError();
          return { error: true };
        }
      }),
    [showError, showNotice]
  );

  return {
    showNotice,
    showError,
    resolveWithNotice,
    resolveWithAction,
  };
}
